import React from "react";
import { motion } from "motion/react";

interface SectionProps {
  id: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  dark?: boolean;
}

export function Section({ id, title, subtitle, children, className = "", dark = false }: SectionProps) {
  return (
    <section 
      id={id} 
      className={`py-32 relative overflow-hidden ${dark ? 'bg-black' : 'bg-transparent'} ${className}`}
    >
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mb-20 text-center lg:text-left"
        >
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-12 relative">
            <div className="absolute -left-12 top-0 text-[12rem] font-black text-foreground/5 pointer-events-none select-none hidden lg:block italic">
              {title.charAt(0)}
            </div>
            <div className="max-w-3xl relative z-10">
              <h2 className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-black mb-8 tracking-tighter uppercase italic leading-[0.85]">
                <span className="text-foreground block">{title.split(' ')[0]}</span>
                {title.split(' ').length > 1 && (
                  <span className="block text-muted-foreground/20 outline-text">{title.split(' ').slice(1).join(' ')}</span>
                )}
              </h2>
              {subtitle && <p className="text-muted-foreground text-lg md:text-xl leading-relaxed font-medium max-w-2xl">{subtitle}</p>}
            </div>
            <div className="hidden lg:block">
              <div className="flex items-center gap-4 text-muted-foreground">
                <span className="text-sm font-black uppercase tracking-[0.5em] rotate-180 [writing-mode:vertical-lr]">Scroll</span>
                <div className="w-[2px] h-32 bg-gradient-to-b from-brand-primary to-background" />
              </div>
            </div>
          </div>
        </motion.div>
        
        {children}
      </div>
    </section>
  );
}